import React, { useMemo, useEffect, useRef } from 'react';
import { PriceHistory, GA4FunnelData, DailyStockData } from '../services/api';

interface PriceHistoryTableProps {
  priceHistory: PriceHistory[];
  ga4Data?: {[date: string]: GA4FunnelData};
  stockData?: {[date: string]: DailyStockData};
}

interface TableData {
  [date: string]: {
    practiced_price?: number | string | null;
    zmsr_price?: number | string | null;
    zpr1_price?: number | string | null;
    zpm3_price?: number | string | null;
    active_type?: string;
    daily_sales?: {
      daily_quantity: number;
      daily_revenue: number;
      daily_sales_count: number;
    };
    ga4_data?: GA4FunnelData;
    stock_data?: DailyStockData;
  };
}


const PriceHistoryTable: React.FC<PriceHistoryTableProps> = ({ priceHistory, ga4Data = {}, stockData = {} }) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  
  const formatDateRange = (dateString: string) => {
    const date = new Date(dateString);
    return date.getDate().toString();
  };


  // Process data to create table structure
  const tableData = useMemo(() => {
    const data: TableData = {};
    
    priceHistory.forEach((item) => {
      if (item.date) {
        data[item.date] = {
          practiced_price: item.practiced_price || undefined,
          zmsr_price: item.zmsr_price || undefined,
          zpr1_price: item.zpr1_price || undefined,
          zpm3_price: item.zpm3_price || undefined,
          active_type: item.active_type,
          daily_sales: item.daily_sales,
          ga4_data: ga4Data[item.date] || { daily_views: 0, daily_add_to_cart: 0, daily_add_to_cart_rate_pct: 0 },
          stock_data: stockData[item.date] || { daily_stock_total: 0 },
        };
      }
    });
    
    return data;
  }, [priceHistory, ga4Data, stockData]);

  // Generate daily columns (no grouping)
  const monthGroups = useMemo(() => {
    if (priceHistory.length === 0) return [];
    
    // Sort data chronologically (newest first for display)
    const sortedData = [...priceHistory].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    
    // Group by month for display
    const groups: { [month: string]: typeof priceHistory } = {};
    
    sortedData.forEach((record) => {
      const date = new Date(record.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!groups[monthKey]) {
        groups[monthKey] = [];
      }
      groups[monthKey].push(record);
    });
    
    return Object.entries(groups).map(([key, monthRecords]) => ({
      key,
      name: new Date(monthRecords[0].date).toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' }),
      records: monthRecords.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()), // Sort days within month oldest first
    })).sort((a, b) => new Date(a.records[0].date).getTime() - new Date(b.records[0].date).getTime());
  }, [priceHistory]);

  // Auto-scroll to today's column (or 75% if today not found) when table loads
  useEffect(() => {
    if (tableContainerRef.current && monthGroups.length > 0) {
      // Small delay to ensure table is rendered
      setTimeout(() => {
        if (tableContainerRef.current) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const todayStr = today.toISOString().split('T')[0];

          // Find index of today's date in all records
          let todayIndex = -1;
          let totalDays = 0;

          for (const group of monthGroups) {
            for (const record of group.records) {
              if (record.date === todayStr) {
                todayIndex = totalDays;
                break;
              }
              totalDays++;
            }
            if (todayIndex !== -1) break;
          }

          // Calculate scroll position
          const container = tableContainerRef.current;
          const scrollWidth = container.scrollWidth - container.clientWidth;

          if (todayIndex !== -1 && totalDays > 0) {
            // Found today - scroll to center it (slightly left of center)
            const columnWidth = scrollWidth / totalDays;
            const scrollToPosition = Math.max(0, (todayIndex * columnWidth) - (container.clientWidth * 0.25));
            container.scrollLeft = scrollToPosition;
          } else {
            // Today not found - scroll to 75% (near end but not fully)
            container.scrollLeft = scrollWidth * 0.75;
          }
        }
      }, 100);
    }
  }, [monthGroups]);

  const formatPrice = (price: number | string | null | undefined) => {
    if (price === undefined || price === null) return '';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return '';
    return numPrice.toFixed(2);
  };



  if (monthGroups.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-muted">Nenhum dado de preço disponível.</p>
      </div>
    );
  }

  return (
    <div 
      ref={tableContainerRef}
      className="table-container" 
      style={{ 
        maxHeight: '700px', 
        overflowX: 'scroll', 
        overflowY: 'auto'
      }}
    >
      <table className="table table-bordered table-sm price-table" style={{ 
        minWidth: 'max-content',
        width: 'max-content'
      }}>
        <thead>
          {/* Month headers */}
          <tr>
            <th className="type-header">Tipo</th>
            {monthGroups.map((group) => (
              <th key={group.key} className="month-header text-center" colSpan={group.records.length}>
                {group.name}
              </th>
            ))}
          </tr>
          
          {/* Day headers */}
          <tr>
            <th className="period-header">Dia</th>
            {monthGroups.map((group) =>
              group.records.map((record, index) => (
                <th key={`${group.key}-${index}`} className="day-header text-center">
                  {formatDateRange(record.date)}
                </th>
              ))
            )}
          </tr>
        </thead>
        
        <tbody>
          {/* ZMSR Row */}
          <tr>
            <td className="row-header zmsr">
              ZMSR
            </td>
            {monthGroups.map((group) =>
              group.records.map((record, index) => {
                const isActive = record.active_type === 'ZMSR';
                return (
                  <td 
                    key={`${group.key}-zmsr-${index}`} 
                    className={`price-cell text-center ${isActive ? 'active' : ''}`}
                    style={isActive ? { fontWeight: 'bold', backgroundColor: '#e3f2fd' } : {}}
                  >
                    {formatPrice(record.zmsr_price)}
                  </td>
                );
              })
            )}
          </tr>
          
          {/* ZPR1 Row */}
          <tr>
            <td className="row-header zpr1">
              ZPR1
            </td>
            {monthGroups.map((group) =>
              group.records.map((record, index) => {
                const isActive = record.active_type === 'ZPR1';
                return (
                  <td 
                    key={`${group.key}-zpr1-${index}`} 
                    className={`price-cell text-center ${isActive ? 'active' : ''}`}
                    style={isActive ? { fontWeight: 'bold', backgroundColor: '#fff3e0' } : {}}
                  >
                    {formatPrice(record.zpr1_price)}
                  </td>
                );
              })
            )}
          </tr>
          
          {/* ZPM3 Row */}
          <tr>
            <td className="row-header zpm3">
              ZPM3
            </td>
            {monthGroups.map((group) =>
              group.records.map((record, index) => {
                const isActive = record.active_type === 'ZPM3';
                return (
                  <td 
                    key={`${group.key}-zpm3-${index}`} 
                    className={`price-cell text-center ${isActive ? 'active' : ''}`}
                    style={isActive ? { fontWeight: 'bold', backgroundColor: '#e8f5e8' } : {}}
                  >
                    {formatPrice(record.zpm3_price)}
                  </td>
                );
              })
            )}
          </tr>
          
          {/* Practiced Price Row */}
          <tr>
            <td className="row-header practiced">
              PRATICADO
            </td>
            {monthGroups.map((group) =>
              group.records.map((record, index) => {
                return (
                  <td 
                    key={`${group.key}-practiced-${index}`} 
                    className="price-cell text-center"
                    style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}
                  >
                    {formatPrice(record.practiced_price)}
                  </td>
                );
              })
            )}
          </tr>
          
          {/* Sales Data Row */}
          <tr>
            <td className="row-header sales">
              VENDAS
            </td>
            {monthGroups.map((group) =>
              group.records.map((record, index) => {
                const salesData = record.daily_sales;
                const quantity = salesData ? (salesData.daily_quantity || 0) : 0;
                const hasSales = quantity > 0;
                
                return (
                  <td 
                    key={`${group.key}-sales-${index}`} 
                    className="price-cell text-center"
                    style={{ 
                      fontSize: '0.8em', 
                      backgroundColor: hasSales ? '#e8f5e8' : '#f8f9fa',
                      color: hasSales ? '#2d5016' : '#6c757d'
                    }}
                  >
                    {quantity.toString()}
                  </td>
                );
              })
            )}
          </tr>
          
          {/* GA4 Views Row */}
          <tr>
            <td className="row-header ga4-views">
              VISUALIZAÇÕES
            </td>
            {monthGroups.map((group) =>
              group.records.map((record, index) => {
                const ga4Data = tableData[record.date]?.ga4_data;
                const views = ga4Data ? ga4Data.daily_views : 0;
                const hasViews = views > 0;
                
                return (
                  <td 
                    key={`${group.key}-views-${index}`} 
                    className="price-cell text-center"
                    style={{ 
                      fontSize: '0.8em', 
                      backgroundColor: hasViews ? '#e3f2fd' : '#f8f9fa',
                      color: hasViews ? '#1565c0' : '#6c757d'
                    }}
                  >
                    {views.toString()}
                  </td>
                );
              })
            )}
          </tr>
          
          {/* GA4 Add to Cart Row */}
          <tr>
            <td className="row-header ga4-cart">
              CARRINHO
            </td>
            {monthGroups.map((group) =>
              group.records.map((record, index) => {
                const ga4Data = tableData[record.date]?.ga4_data;
                const addToCart = ga4Data ? ga4Data.daily_add_to_cart : 0;
                const hasAddToCart = addToCart > 0;
                
                return (
                  <td 
                    key={`${group.key}-cart-${index}`} 
                    className="price-cell text-center"
                    style={{ 
                      fontSize: '0.8em', 
                      backgroundColor: hasAddToCart ? '#fff3e0' : '#f8f9fa',
                      color: hasAddToCart ? '#e65100' : '#6c757d'
                    }}
                  >
                    {addToCart.toString()}
                  </td>
                );
              })
            )}
          </tr>
          
          {/* GA4 Conversion Rate Row */}
          <tr>
            <td className="row-header ga4-rate">
              TX CARRINHO
            </td>
            {monthGroups.map((group) =>
              group.records.map((record, index) => {
                const ga4Data = tableData[record.date]?.ga4_data;
                const rate = ga4Data ? ga4Data.daily_add_to_cart_rate_pct : 0;
                const hasRate = rate > 0;
                
                return (
                  <td 
                    key={`${group.key}-rate-${index}`} 
                    className="price-cell text-center"
                    style={{ 
                      fontSize: '0.8em', 
                      backgroundColor: hasRate ? '#e8f5e8' : '#f8f9fa',
                      color: hasRate ? '#2d5016' : '#6c757d'
                    }}
                  >
                    {rate > 0 ? `${rate.toFixed(1)}%` : '0%'}
                  </td>
                );
              })
            )}
          </tr>
          
          {/* Stock Row */}
          <tr>
            <td className="row-header stock">
              STOCK
            </td>
            {monthGroups.map((group) =>
              group.records.map((record, index) => {
                const stockData = tableData[record.date]?.stock_data;
                const stockTotal = stockData ? stockData.daily_stock_total : 0;
                const hasStock = stockTotal > 0;
                
                return (
                  <td 
                    key={`${group.key}-stock-${index}`} 
                    className="price-cell text-center"
                    style={{ 
                      fontSize: '0.8em', 
                      backgroundColor: hasStock ? '#fff9c4' : '#f8f9fa',
                      color: hasStock ? '#2d5016' : '#6c757d'
                    }}
                  >
                    {stockTotal.toString()}
                  </td>
                );
              })
            )}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default PriceHistoryTable;